import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Save, Building2,
  Database, AlertTriangle, CheckCircle, LogOut, Shield,
  Download, FolderOpen,
} from 'lucide-react';
import { useConfig } from '@/hooks/useData';
import {
  exportarDatos, importarDatos, getUltimaExportacion, setUltimaExportacion,
} from '@/lib/storage';
import { destroySession } from '@/lib/auth';
import { DEFAULT_LOGO_URL } from '@/lib/defaultLogo';
import { formatFecha } from '@/lib/calc';

export default function Configuracion() {
  const navigate = useNavigate();
  const { config, update } = useConfig();
  // Sincronizar formulario con la config guardada al montar
  const [form, setForm] = useState({ ...config });
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [importConfirm, setImportConfirm] = useState(false);
  const [importData, setImportData] = useState<string | null>(null);
  const [importError, setImportError] = useState('');
  const [importOk, setImportOk] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const ultimaExport = getUltimaExportacion();

  // Campos obligatorios para que los presupuestos salgan completos
  const camposFaltantes = [
    !form.nombre && 'Nombre de empresa',
    !form.nif && 'NIF / CIF',
    !form.telefono && 'Teléfono',
    !form.direccion && 'Dirección',
    !form.ciudad && 'Ciudad',
    !form.email && 'Email',
    !form.iban && 'IBAN',
  ].filter(Boolean) as string[];

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm(f => ({ ...f, [k]: v }));
    setSaved(false);
    setHasChanges(true);
  };

  const handleSave = () => {
    update(form);
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  // ── Exportar ──────────────────────────────────────────────────────────────
  const handleExport = () => {
    const backup = exportarDatos();
    const json = JSON.stringify(backup, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `presupuesto-pro-backup-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setUltimaExportacion();
  };

  // ── Importar ─────────────────────────────────────────────────────────────
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setImportData(ev.target?.result as string);
      setImportError('');
      setImportConfirm(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const confirmImport = () => {
    try {
      const backup = JSON.parse(importData!);
      importarDatos(backup);
      setImportOk(true);
      setTimeout(() => { setImportOk(false); window.location.reload(); }, 1800);
    } catch {
      setImportError('El archivo no es una copia de seguridad válida.');
    }
    setImportConfirm(false);
  };

  const handleLogout = () => { destroySession(); window.location.reload(); };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30"
        style={{ boxShadow: '0 1px 8px -2px color-mix(in srgb, var(--foreground) 5%, transparent)' }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-9 w-9">
              <ArrowLeft size={18} />
            </Button>
            <span className="font-semibold text-sm">Configuración</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setLogoutConfirm(true)} className="gap-1.5 text-muted-foreground">
              <LogOut size={15} /> Cerrar sesión
            </Button>
            <Button size="sm" onClick={handleSave} className={`gap-1.5 ${hasChanges ? 'animate-pulse' : ''}`}>
              <Save size={15} />
              {saved ? <span className="text-emerald-100">✓ Guardado</span> : hasChanges ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── Banner datos guardados ── */}
        {saved && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800"
          >
            <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Configuración guardada correctamente</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-400">Los datos de empresa se aplicarán a todos los presupuestos generados.</p>
            </div>
          </motion.div>
        )}

        {/* ── Banner campos faltantes ── */}
        {camposFaltantes.length > 0 && !saved && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Datos de empresa incompletos</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Los siguientes campos aparecerán en blanco en tus presupuestos PDF:
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {camposFaltantes.map(campo => (
                  <span key={campo} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
                    {campo}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        <Card title="Logo de empresa" icon={<Building2 size={16} />}>
          <div className="flex items-center gap-5">
            {/* Vista previa del logo — solo lectura */}
            <div className="h-20 w-20 rounded-xl border border-border flex items-center justify-center bg-muted overflow-hidden shrink-0">
              <img src={DEFAULT_LOGO_URL} alt="Logo corporativo" className="h-full w-full object-contain" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Shield size={14} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Logo corporativo protegido</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                El logo de <strong>Reparaciones y Reformas Oscar Alaminos</strong> está fijado como imagen oficial
                de la empresa y aparecerá en todos los presupuestos PDF generados.<br />
                No puede ser modificado desde la aplicación para garantizar la identidad corporativa.
              </p>
            </div>
          </div>
        </Card>

        {/* ── Datos empresa ── */}
        <Card title="Datos de la empresa" icon={<Building2 size={16} />}>
          <p className="text-xs text-muted-foreground mb-4 pb-3 border-b border-border">
            Estos datos aparecerán automáticamente en <strong>todos los presupuestos PDF</strong> que generes.
            Los campos marcados con <span className="text-destructive font-bold">*</span> son importantes para que el PDF sea profesional.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label={<>Nombre / Razón social <span className="text-destructive">*</span></>} className="sm:col-span-2">
              <Input value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Reparaciones y Reformas Oscar Alminos"
                className={!form.nombre ? 'border-amber-300 focus-visible:ring-amber-300' : ''} />
            </F>
            <F label={<>NIF / CIF <span className="text-destructive">*</span></>}>
              <Input value={form.nif} onChange={e => set('nif', e.target.value)} placeholder="12345678A" className={`font-mono ${!form.nif ? 'border-amber-300 focus-visible:ring-amber-300' : ''}`} />
            </F>
            <F label={<>Teléfono <span className="text-destructive">*</span></>}>
              <Input value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="600 000 000"
                className={!form.telefono ? 'border-amber-300 focus-visible:ring-amber-300' : ''} />
            </F>
            <F label={<>Dirección <span className="text-destructive">*</span></>} className="sm:col-span-2">
              <Input value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle Mayor, 1"
                className={!form.direccion ? 'border-amber-300 focus-visible:ring-amber-300' : ''} />
            </F>
            <F label="Código postal">
              <Input value={form.cp} onChange={e => set('cp', e.target.value)} placeholder="28001" className="font-mono" />
            </F>
            <F label={<>Ciudad <span className="text-destructive">*</span></>}>
              <Input value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Madrid"
                className={!form.ciudad ? 'border-amber-300 focus-visible:ring-amber-300' : ''} />
            </F>
            <F label={<>Email <span className="text-destructive">*</span></>}>
              <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="info@empresa.com"
                className={!form.email ? 'border-amber-300 focus-visible:ring-amber-300' : ''} />
            </F>
            <F label="Sitio web">
              <Input value={form.web} onChange={e => set('web', e.target.value)} placeholder="www.empresa.com" />
            </F>
          </div>
        </Card>

        {/* ── Datos bancarios ── */}
        <Card title="Datos bancarios" icon={<Shield size={16} />}>
          <F label="IBAN">
            <Input value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" className="font-mono tracking-wide" />
          </F>
          <p className="text-xs text-muted-foreground mt-2">El IBAN aparecerá en el bloque de datos bancarios del PDF del presupuesto.</p>
        </Card>

        {/* ── Personalización PDF ── */}
        <Card title="Diseño del PDF" icon={<Building2 size={16} />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Color corporativo principal">
              <div className="flex items-center gap-3">
                <input type="color" value={form.colorPrimario} onChange={e => set('colorPrimario', e.target.value)} className="h-10 w-16 rounded border border-border cursor-pointer p-0.5" />
                <Input value={form.colorPrimario} onChange={e => set('colorPrimario', e.target.value)} className="font-mono flex-1" placeholder="#2E5F5E" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Color de cabecera, títulos y total en el PDF.</p>
            </F>
            <F label="Pie de página del PDF">
              <Textarea value={form.piePagina} onChange={e => set('piePagina', e.target.value)} rows={3} placeholder="Gracias por confiar en nosotros." />
            </F>
          </div>
        </Card>

        {/* ── Copia de seguridad ── */}
        <Card title="Copia de seguridad de datos" icon={<Database size={16} />}>
          <p className="text-sm text-muted-foreground mb-4">
            Todos tus datos (clientes, productos, presupuestos y configuración) se guardan automáticamente en este navegador.
            Realiza copias de seguridad periódicas para no perder la información si cambias de dispositivo o borras el caché.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Exportar */}
            <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
              <div className="flex items-center gap-2">
                <Download size={16} className="text-primary" />
                <span className="font-semibold text-sm">Exportar datos</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Descarga un archivo JSON con todos tus datos. Guárdalo en un lugar seguro.
              </p>
              {ultimaExport && (
                <p className="text-xs text-muted-foreground">
                  Última exportación: <strong>{formatFecha(ultimaExport)}</strong>
                </p>
              )}
              <Button onClick={handleExport} variant="outline" size="sm" className="w-full gap-2">
                <Download size={14} /> Descargar copia de seguridad
              </Button>
            </div>

            {/* Importar */}
            <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
              <div className="flex items-center gap-2">
                <FolderOpen size={16} className="text-amber-600" />
                <span className="font-semibold text-sm">Restaurar datos</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Restaura una copia de seguridad. <strong className="text-destructive">Reemplaza todos los datos actuales.</strong>
              </p>
              {importError && <p className="text-xs text-destructive font-medium">{importError}</p>}
              {importOk && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <CheckCircle size={13} /> ¡Datos restaurados! Recargando…
                </motion.p>
              )}
              <Button onClick={() => importRef.current?.click()} variant="outline" size="sm" className="w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50">
                <FolderOpen size={14} /> Restaurar desde archivo
              </Button>
              <input ref={importRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImportFile} />
            </div>
          </div>

          <Separator className="my-4" />

          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
            <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
            <span>
              Los datos se almacenan localmente en este navegador. Si borras el historial/caché o cambias de navegador o dispositivo,
              los datos dejarán de estar disponibles. <strong>Realiza una exportación semanal</strong> para mantener una copia segura.
            </span>
          </div>
        </Card>

        {/* ── Sesión ── */}
        <Card title="Seguridad y sesión" icon={<Shield size={16} />}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Sesión activa</p>
              <p className="text-xs text-muted-foreground mt-0.5">La sesión se cierra automáticamente tras 8 horas de inactividad.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setLogoutConfirm(true)} className="gap-1.5 shrink-0">
              <LogOut size={15} /> Cerrar sesión
            </Button>
          </div>
        </Card>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate('/')}>Volver</Button>
          <Button onClick={handleSave} className="gap-1.5">
            <Save size={15} /> {saved ? '¡Guardado!' : 'Guardar configuración'}
          </Button>
        </div>
      </main>

      {/* Confirm restaurar */}
      <AlertDialog open={importConfirm} onOpenChange={setImportConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" /> ¿Restaurar datos?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esto reemplazará <strong>todos</strong> los presupuestos, clientes, productos y la configuración con los datos del archivo.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmImport} className="bg-amber-600 text-white hover:bg-amber-700">
              Sí, restaurar datos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm cerrar sesión */}
      <AlertDialog open={logoutConfirm} onOpenChange={setLogoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
            <AlertDialogDescription>Tendrás que volver a introducir la contraseña para acceder.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>Cerrar sesión</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-muted/30">
        <span className="text-primary">{icon}</span>
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function F({ label, children, className = '' }: { label: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
